import { Typography, List, Button } from '@mui/material';
import { Stack } from '@mui/system';
import React, { useMemo, useState } from 'react';
import { useGetHospitalsInOrganizationQuery, useGetRecommendationBatchesQuery } from '../../../../api/apiSlice';
import { useParams } from 'react-router';
import BatchEntry from './BatchEntry';
import { theme } from '../../../../theme';
import { AddRounded } from '@mui/icons-material';
import CreateBatchDialog from './CreateBatchDialog';

function RecommendationBatchTracking() {
    const organizationId = useParams().organizationId;
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const { data: batches = [] } = useGetRecommendationBatchesQuery(organizationId, { pollingInterval: 60000 });
    const { data: hospitals = [] } = useGetHospitalsInOrganizationQuery(organizationId);

    const batchesByDateGroups = useMemo(() => {
        const groups = {
            'Today': [],
            'Last 7 Days': [],
            'Last 30 Days': [],
            'Older': []
        };

        const now = new Date();
        
        batches.forEach((batch) => {
            const createdAt = new Date(batch.created_at);
            const diffTime = Math.abs(now - createdAt);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            
            if (diffDays <= 1) {
                groups['Today'].push(batch);
            } else if (diffDays <= 7) {
                groups['Last 7 Days'].push(batch);
            } else if (diffDays <= 30) {
                groups['Last 30 Days'].push(batch);
            } else {
                groups['Older'].push(batch);
            }
        });

        return groups;
    }, [batches]);

    const steps = [
        { key: 'card_item_stats', label: 'Card Stats' },
        { key: 'procedure_item_stats', label: 'Procedure Stats' },
        { key: 'time_series_recommendations', label: 'Time Series' },
        { key: 'card_comparison_recommendations', label: 'Card Comparison' },
        { key: 'cleanup_stale_recommendations', label: 'Stale Rec Cleanup' },
    ]

    return (
        <Stack px={4} spacing={4}>
            <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Stack spacing={0.5}>
                    <Typography variant='heavy' fontSize='24px'>Recommendation Batch Tracking</Typography>
                    <Typography variant='normal' color={theme.palette.subtext.main} fontSize='14px'>This page will auto-update every minute with the current status of rec generation.</Typography>    
                </Stack>
                <Button variant='contained' onClick={() => setIsDialogOpen(true)} startIcon={<AddRounded />}>Begin New Batch</Button>
                <CreateBatchDialog open={isDialogOpen} setOpen={setIsDialogOpen} hospitals={hospitals} />
            </Stack>
            
            { Object.keys(batchesByDateGroups).map((dateGroup) => (
                <Stack spacing={2}>
                    <Typography variant='heavy' fontSize='20px'>{dateGroup}</Typography>
                    <List>
                        { batchesByDateGroups[dateGroup].length === 0 &&
                            <Typography>No batches in this time frame.</Typography>
                        }
                        { batchesByDateGroups[dateGroup].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((batch) => (
                            <BatchEntry key={batch.id} batch={batch} steps={steps} />
                        )) }
                    </List>
                </Stack>
            ))}                
        </Stack>
    );
}

export default RecommendationBatchTracking;
